import React from "react";

/**
 * ErrorBoundary — catches any render-time exception below it and renders a
 * calm fallback panel instead of letting React unmount the whole tree (the
 * "white screen of death"). On crash it also drops a small breadcrumb into
 * `localStorage.__rocklink_lastError` so the next session can recover the
 * stack trace from DevTools without needing user repro.
 *
 * Wrap pages that have heavy filtering/render logic (Dashboard, StagePage)
 * — anything where a single bad data-row could otherwise blank the screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Persist enough context to reproduce later — keep payload tiny so we
    // don't blow the localStorage quota on noisy stacks.
    try {
      const payload = {
        ts: new Date().toISOString(),
        url: typeof window !== "undefined" ? window.location.href : "",
        message: error?.message || String(error),
        stack: (error?.stack || "").split("\n").slice(0, 12).join("\n"),
        componentStack: (info?.componentStack || "").split("\n").slice(0, 12).join("\n"),
      };
      window.localStorage.setItem("__rocklink_lastError", JSON.stringify(payload));
    } catch (_) { /* quota / SSR */ }
    // Mirror to the console so DevTools captures it on the spot.
    // eslint-disable-next-line no-console
    console.error("[RockLink ErrorBoundary]", error, info);
  }

  reset = () => {
    // Wipe anything that could re-trigger the same crash on next render —
    // global filter state, in-memory deep-link refs, then force a full
    // re-render of children.
    try { window.localStorage.removeItem("rocklink.globalFilter"); } catch (_) {}
    this.setState({ error: null });
  };

  hardReload = () => {
    // Last-resort escape hatch: clean URL params (deep-link `?item=`) and
    // reload to a known-good route.
    try { window.location.replace("/"); }
    catch (_) { window.location.reload(); }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const msg = error?.message || String(error);
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-4"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg shadow-sm p-6 text-center">
          <div className="mx-auto mb-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/15 text-amber-600 text-xl font-bold">
            !
          </div>
          <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Something glitched</h2>
          <p className="mt-1 text-[13px] text-[hsl(var(--muted-foreground))]">
            A render error occurred — most likely from the active filter combination.
            Your data is safe; reset filters to recover.
          </p>
          <pre className="mt-3 text-[10px] leading-tight text-left bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))] rounded px-2 py-1.5 overflow-auto max-h-24">
            {msg}
          </pre>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="px-3 py-1.5 text-[12px] font-bold rounded-md bg-orange-600 text-white hover:bg-orange-500 transition-colors"
              data-testid="error-boundary-reset"
            >
              Reset filters
            </button>
            <button
              type="button"
              onClick={this.hardReload}
              className="px-3 py-1.5 text-[12px] font-bold rounded-md border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/40 transition-colors"
              data-testid="error-boundary-reload"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
